/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { useNavigate } from "react-router";
import {
  RiUser3Line,
  RiLockPasswordLine,
  RiCheckLine,
  RiShieldKeyholeLine,
  RiCloseCircleLine,
  RiAlertLine,
} from "@remixicon/react";
import { useAuthStore, type User } from "@/stores/authStore";
// WebP comprimido (~54KB) en lugar del PNG de 250KB. loading="lazy" y el
// contenedor oculto en móvil evitan descargarla cuando no se muestra.
import loginImage from "@/assets/loginImage2.webp";
import { toast } from "sonner";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

/** DTO que devuelve el backend */
interface LoginResponseDto {
  token: string;
  expiresAt: string;
  user: User;
}

// Estilos en línea para máxima compatibilidad
const styles: { [key: string]: React.CSSProperties } = {
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    fontFamily: "Arial, Helvetica, sans-serif",
  },
  header: {
    position: "absolute",
    top: "16px",
    left: "16px",
    zIndex: 50,
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#1877f2",
  },
  loginContainer: {
    width: "50%",
    maxWidth: "400px",
    padding: "16px",
  },
  card: {
    padding: "24px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    borderRadius: "8px",
  },
  cardTitleContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
  },
  cardTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    marginLeft: "8px",
  },
  formControl: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    marginBottom: "4px",
  },
  inputContainer: {
    position: "relative",
  },
  input: {
    width: "100%",
    padding: "10px 40px 10px 36px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    boxSizing: "border-box",
  },
  inputIconLeft: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#888",
  },
  inputIconRight: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
  },
  button: {
    width: "100%",
    padding: "12px",
    fontSize: "16px",
    backgroundColor: "#1877f2",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  error: {
    backgroundColor: "#ffebe8",
    border: "1px solid #ffc9c4",
    color: "#c92a2a",
    padding: "12px",
    marginBottom: "16px",
    borderRadius: "4px",
  },
  capsLockWarning: {
    marginTop: "4px",
    display: "flex",
    alignItems: "center",
    color: "#c92a2a",
    fontSize: "14px",
  },
  footer: {
    marginTop: "16px",
    textAlign: "center",
    color: "#888",
    fontSize: "12px",
  },
};

export const Login: React.FC<{
  autoUsuario?: string;
  autoPassword?: string;
}> = ({ autoUsuario, autoPassword }) => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const usuarioInputRef = useRef<HTMLInputElement>(null);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [usuario, setUsuario] = useState(autoUsuario || "");
  const [password, setPassword] = useState(autoPassword || "");
  const [usePassword, setUsePassword] = useState(false);
  const [usuarioTouched, setUsuarioTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (usuarioInputRef.current) {
      usuarioInputRef.current.focus();
    }
  }, []);

  // Login automático si vienen los parámetros
  useEffect(() => {
    if (autoUsuario) {
      setUsuarioTouched(true);
      setUsePassword(!!autoPassword);
      handleSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoUsuario, autoPassword]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.getModifierState("CapsLock")) {
      setCapsLockOn(true);
    } else {
      setCapsLockOn(false);
    }
  };

  const isUsuarioValid = () => {
    const t = usuario.trim();
    if (t.length === 0) return false;
    return t.includes("@")
      ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)
      : /^[a-zA-Z0-9_-]{3,}$/.test(t);
  };

  const showErrorUsuario = usuarioTouched && !isUsuarioValid();
  const showSuccessUsuario = usuarioTouched && isUsuarioValid();

  const handleSubmit = async (e: React.FormEvent) => {
    if (e && e.preventDefault) e.preventDefault();
    setUsuarioTouched(true);
    setError("");
    if (!isUsuarioValid()) {
      return setError("Usuario o correo inválido");
    }

    setLoading(true);
    try {
      const jsonbody = {
        username: usuario.trim(),
        ...(usePassword && password && { password: password.trim() }),
      };

      const loginPromise = fetch(
        `${import.meta.env.VITE_API_URL}/api/auth/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(jsonbody),
        }
      );

      await toast.promise(loginPromise, {
        loading: "Iniciando sesión...",
        success: async (res) => {
          if (!res.ok) {
            const errorData = await res.text();
            throw new Error(`Error ${res.status}: ${errorData}`);
          }
          const data = (await res.json()) as LoginResponseDto;
          setAuth(data.user, data.token, data.expiresAt);
          navigate("/home");
          return "Sesión iniciada correctamente";
        },
        error: (err) => `Error al iniciar sesión: ${err.message}`,
        finally: () => {
          setLoading(false);
        },
      });
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error al iniciar sesión");
      setLoading(false);
    }
  };

  const getInputStyle = () => {
    const style = { ...styles.input };
    if (showSuccessUsuario) {
      style.borderColor = "#28a745";
    } else if (showErrorUsuario) {
      style.borderColor = "#dc3545";
    }
    return style;
  };

  return (
    <div
      className="login-root"
      style={styles.root}
      role="main"
      aria-label="Formulario de inicio de sesión"
    >
      {loading && <LoadingSpinner />}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>{import.meta.env.VITE_EMPRESA}</h1>
      </div>

      {/* hidden en móvil, flex 50% en >=768px. Sin display inline para que
          las clases de Tailwind no pierdan especificidad. */}
      <div className="hidden md:flex md:w-1/2 items-center justify-center p-8">
        <img
          src={loginImage}
          alt="Bienvenida"
          style={{ maxWidth: "450px", width: "100%" }}
          loading="lazy"
          decoding="async"
        />
      </div>

      <div style={styles.loginContainer} className="login-container">
        <div style={styles.card} className="login-card">
          <div style={styles.cardTitleContainer}>
            <RiShieldKeyholeLine size={28} color="#1877f2" />
            <h2 style={styles.cardTitle} className="login-card-title">
              Bienvenido de nuevo
            </h2>
          </div>

          {error && (
            <div style={styles.error}>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={styles.formControl}>
              <label style={styles.label} htmlFor="usuario">
                Usuario
              </label>
              <div style={styles.inputContainer}>
                <span style={styles.inputIconLeft}>
                  <RiUser3Line />
                </span>
                <input
                  id="usuario"
                  ref={usuarioInputRef}
                  type="text"
                  placeholder="USUARIO"
                  style={getInputStyle()}
                  value={usuario}
                  onChange={(e) => {
                    setUsuarioTouched(true);
                    setUsuario(e.target.value);
                  }}
                  required
                />
                <span style={styles.inputIconRight}>
                  {showErrorUsuario && <RiCloseCircleLine color="#dc3545" />}
                  {showSuccessUsuario && <RiCheckLine color="#28a745" />}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "12px",
                marginBottom: "16px",
              }}
            >
              <input
                id="toggle-pass"
                type="checkbox"
                style={{ marginRight: "8px" }}
                checked={usePassword}
                onChange={(e) => {
                  const v = e.target.checked;
                  setUsePassword(v);
                  if (!v) {
                    setPassword("");
                    setCapsLockOn(false);
                  }
                }}
                aria-describedby="toggle-pass-label"
              />
              <label
                htmlFor="toggle-pass"
                id="toggle-pass-label"
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Necesita contraseña
              </label>
            </div>

            {usePassword && (
              <div style={styles.formControl}>
                <label style={styles.label} htmlFor="password">
                  Contraseña
                </label>
                <div style={styles.inputContainer}>
                  <span style={styles.inputIconLeft}>
                    <RiLockPasswordLine />
                  </span>
                  <input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    style={styles.input}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyDown}
                  />
                </div>
                {capsLockOn && (
                  <div style={styles.capsLockWarning}>
                    <RiAlertLine size={14} style={{ marginRight: "4px" }} />
                    <span>Bloqueo de mayúsculas activado</span>
                  </div>
                )}
              </div>
            )}

            <button type="submit" style={styles.button} disabled={loading}>
              {loading ? "Cargando..." : "Iniciar sesión"}
            </button>
          </form>

          <div style={styles.footer}>
            {import.meta.env.VITE_EMPRESA} &copy; {new Date().getFullYear()}
            <span
              style={{
                fontSize: "10px",
                color: "#aaa",
                marginLeft: "8px",
                float: "right",
                userSelect: "none",
                marginTop: "2px",
              }}
            >
              Versión {import.meta.env.VITE_APP_VERSION || "1.0.0"}
            </span>
          </div>
        </div>
      </div>
      <style>{`
        /* Fallback legacy: blanco */
        .login-root { background: #ffffff; }
        /* Modern themes */
        @media (prefers-color-scheme: light) {
          .login-root { background: #f8fafc; }
          .login-card { background:#ffffff; border:1px solid #e5e7eb; color:#0f172a; }
          .login-card-title { color:#0f172a; }
        }
        @media (prefers-color-scheme: dark) {
          .login-root { background: #0b1220; }
          .login-card { background:#0f172a; border:1px solid #334155; color:#e5e7eb; }
          .login-card-title { color:#e5e7eb; }
        }
        /* Legacy fallback: blanco con botón azul */
        .login-card { background:#ffffff; border:1px solid #e5e7eb; color:#0f172a; }
        .login-card-title { color:#0f172a; }

        /* ===== Móvil: misma identidad, layout cómodo y táctil (CSS puro) ===== */
        @media (max-width: 768px) {
          /* Empuja el contenido bajo el header absoluto para que no se encime */
          .login-root { align-items: flex-start; padding-top: 64px; }
          .login-container {
            width: 100% !important;
            max-width: 440px;
            margin: 0 auto;
          }
          .login-card {
            padding: 20px !important;
            border-radius: 14px !important;
            box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08) !important;
          }
          /* 16px evita el zoom automático de iOS al enfocar; alturas táctiles */
          .login-card input[type="text"],
          .login-card input[type="password"] {
            font-size: 16px !important;
            min-height: 46px;
          }
          .login-card button[type="submit"] { min-height: 48px; }
        }
      `}</style>
    </div>
  );
};

// Wrapper para login automático por parámetros
export function LoginWrapper() {
  const [searchParams] = useSearchParams();
  const usuario = searchParams.get("nombre_usuario") || undefined;
  const password = searchParams.get("contrasenia") || undefined;
  return <Login autoUsuario={usuario} autoPassword={password} />;
}
